/**
 * Parcel data categories and columns (aligned with ParcelDataViewer.tsx).
 * Use this as the single source of truth for enrichment category/column alignment.
 */
export const PARCEL_DATA_CATEGORIES = [
	{
		name: "Property characteristics",
		iconKey: "Home" as const,
		columns: [
			{ label: "Use Description", key: "usedesc" },
			{ label: "Use Code", key: "usecode" },
			{ label: "Year Built", key: "yearbuilt" },
			{ label: "Structure Style", key: "structstyle" },
			{ label: "Number of Stories", key: "numstories" },
			{ label: "Number of Rooms", key: "numrooms" },
			{ label: "Number of Units", key: "numunits" },
			{ label: "Square Feet (GIS)", key: "ll_gissqft" },
			{ label: "Acreage (GIS)", key: "ll_gisacre" },
			{ label: "Zoning", key: "zoning" },
			{ label: "Zoning Description", key: "zoning_description" },
			{ label: "Subdivision", key: "subdivision" },
			{ label: "Lot", key: "lot" },
			{ label: "Block", key: "block" },
		],
	},
	{
		name: "Financial",
		iconKey: "DollarSign" as const,
		columns: [
			{ label: "Property Value", key: "parval" },
			{ label: "Land Value", key: "landval" },
			{ label: "Improvement Value", key: "improvval" },
			{ label: "Agricultural Value", key: "agval" },
			{ label: "Tax Amount", key: "taxamt" },
			{ label: "Tax Year", key: "taxyear" },
			{ label: "Sale Price", key: "saleprice" },
			{ label: "Sale Date", key: "saledate" },
			{ label: "Value Type", key: "parvaltype" },
		],
	},
	{
		name: "Sales",
		iconKey: "TrendingUp" as const,
		columns: [
			{ label: "Last sale date", key: "saledate" },
			{ label: "Last sale price", key: "saleprice" },
		],
	},
	{
		name: "Location & address",
		iconKey: "MapPin" as const,
		columns: [
			{ label: "Full Address", key: "address" },
			{ label: "City", key: "scity" },
			{ label: "State", key: "state2" },
			{ label: "ZIP Code", key: "szip5" },
			{ label: "County", key: "county" },
			{ label: "Latitude", key: "lat" },
			{ label: "Longitude", key: "lon" },
			{ label: "Street Number", key: "saddno" },
			{ label: "Street Name", key: "saddstr" },
			{ label: "Street Type", key: "saddsttyp" },
			{ label: "Unit", key: "sunit" },
		],
	},
	{
		name: "Owner & mailing",
		iconKey: "User" as const,
		columns: [
			{ label: "Primary Owner", key: "owner" },
			{ label: "Owner 2", key: "owner2" },
			{ label: "Owner 3", key: "owner3" },
			{ label: "Owner 4", key: "owner4" },
			{ label: "Owner Type", key: "owntype" },
			{ label: "Previous Owner", key: "previous_owner" },
			{ label: "Care Of", key: "careof" },
		],
	},
	{
		name: "Mailing address",
		iconKey: "MapPin" as const,
		columns: [
			{ label: "Mailing Address", key: "mailadd" },
			{ label: "Mailing City", key: "mail_city" },
			{ label: "Mailing State", key: "mail_state2" },
			{ label: "Mailing ZIP", key: "mail_zip" },
			{ label: "Mailing Country", key: "mail_country" },
		],
	},
	{
		name: "Geographic & census",
		iconKey: "Users" as const,
		columns: [
			{ label: "GEO ID", key: "geoid" },
			{ label: "Census Tract", key: "census_tract" },
			{ label: "Census Block", key: "census_block" },
			{ label: "Census Block Group", key: "census_blockgroup" },
			{ label: "Census ZCTA", key: "census_zcta" },
			{ label: "Opportunity Zone", key: "qoz" },
			{ label: "QOZ Tract", key: "qoz_tract" },
			{ label: "Neighborhood", key: "neighborhood" },
		],
	},
	{
		name: "Legal & assessor",
		iconKey: "FileText" as const,
		columns: [
			{ label: "Parcel Number", key: "parcelnumb" },
			{
				label: "Parcel (No Formatting)",
				key: "parcelnumb_no_formatting",
			},
			{ label: "Account Number", key: "account_number" },
			{ label: "Tax ID", key: "tax_id" },
			{ label: "Alternative Parcel 1", key: "alt_parcelnumb1" },
			{ label: "Alternative Parcel 2", key: "alt_parcelnumb2" },
			{ label: "Alternative Parcel 3", key: "alt_parcelnumb3" },
			{ label: "State Parcel Number", key: "state_parcelnumb" },
			{ label: "Book", key: "book" },
			{ label: "Page", key: "page" },
			{ label: "Plat", key: "plat" },
		],
	},
] as const;

/**
 * Enrichment column metadata aligned with parcel/property data structure.
 * Keys match ParcelDataViewer field keys and common API column keys.
 */
export const ENRICHMENT_COLUMN_DESCRIPTIONS: Record<string, string> = {
	// Property characteristics (ParcelDataViewer PROPERTY_CHARACTERISTICS)
	usedesc: "Use description (e.g. residential, commercial)",
	usecode: "Use code",
	yearbuilt: "Year the structure was built",
	structstyle: "Structure style",
	numstories: "Number of stories",
	numrooms: "Number of rooms",
	numunits: "Number of units",
	ll_gissqft: "Square footage from GIS",
	ll_gisacre: "Acreage from GIS",
	zoning: "Zoning classification",
	zoning_description: "Zoning description",
	subdivision: "Recorded subdivision name",
	lot: "Lot number",
	block: "Block number",
	// Financial (ParcelDataViewer FINANCIAL_DETAILS)
	parval: "Property value / assessed value",
	landval: "Land value",
	improvval: "Improvement value",
	agval: "Agricultural value",
	taxamt: "Tax amount",
	taxyear: "Tax year",
	saleprice: "Sale price / last sale price",
	saledate: "Sale date / last sale date",
	parvaltype: "Value type",
	// Location (ParcelDataViewer LOCATION_ADDRESS)
	address: "Full street address",
	scity: "City",
	state2: "State",
	szip5: "ZIP code",
	county: "County name",
	lat: "Latitude",
	lon: "Longitude",
	saddno: "Street number",
	saddstr: "Street name",
	saddsttyp: "Street type",
	sunit: "Unit",
	// Owner (ParcelDataViewer OWNER_MAILING)
	owner: "Primary owner name",
	owner2: "Owner 2",
	owner3: "Owner 3",
	owner4: "Owner 4",
	owntype: "Owner type",
	previous_owner: "Previous owner",
	careof: "Care of",
	// Mailing (ParcelDataViewer MAILING_ADDRESS)
	mailadd: "Mailing address",
	mail_city: "Mailing city",
	mail_state2: "Mailing state",
	mail_zip: "Mailing ZIP",
	mail_country: "Mailing country",
	// Geographic & census (ParcelDataViewer GEOGRAPHIC_CENSUS)
	geoid: "GEO ID",
	census_tract: "Census tract",
	census_block: "Census block",
	census_blockgroup: "Census block group",
	census_zcta: "Census ZCTA",
	qoz: "Opportunity zone",
	qoz_tract: "QOZ tract",
	neighborhood: "Neighborhood",
	// Legal (ParcelDataViewer LEGAL_INFO)
	parcelnumb: "Parcel number / APN",
	parcelnumb_no_formatting: "Parcel number without formatting",
	account_number: "Account number",
	tax_id: "Tax ID",
	alt_parcelnumb1: "Alternative parcel number 1",
	alt_parcelnumb2: "Alternative parcel number 2",
	alt_parcelnumb3: "Alternative parcel number 3",
	state_parcelnumb: "State parcel number",
	book: "Book",
	page: "Page",
	plat: "Plat",
	// Common API / display aliases
	owner_name: "Full name of property owner",
	ownerName: "Full name of property owner",
	owner_phone: "Phone number on file for the owner",
	ownerPhone: "Phone number on file for the owner",
	owner_email: "Email address on file for the owner",
	ownerEmail: "Email address on file for the owner",
	mailing_address: "Address where tax bills and notices are sent",
	mailingAddress: "Address where tax bills and notices are sent",
	parcel_id: "Official parcel or APN identifier from the county",
	parcelId: "Official parcel or APN identifier from the county",
	year_built: "Construction year of the primary structure",
	yearBuilt: "Construction year of the primary structure",
	square_footage: "Living area in square feet",
	squareFootage: "Living area in square feet",
	lot_size: "Total lot or land area",
	lotSize: "Total lot or land area",
	bedrooms: "Number of bedrooms",
	bathrooms: "Number of bathrooms",
	property_type: "Use type (e.g. Residential, Commercial)",
	propertyType: "Use type (e.g. Residential, Commercial)",
	stories: "Number of stories",
	garage_spaces: "Number of garage spaces",
	garageSpaces: "Number of garage spaces",
	pool: "Whether the property has a pool",
	formatted_address: "Standardized full street address",
	formattedAddress: "Standardized full street address",
	state: "State name",
	city: "City or municipality",
	zip: "ZIP or postal code",
	zip_code: "ZIP or postal code",
	zipCode: "ZIP or postal code",
	assessed_value: "Current assessed value for taxation",
	assessedValue: "Current assessed value for taxation",
	market_value: "Estimated market value",
	marketValue: "Estimated market value",
	tax_amount: "Current year tax amount",
	taxAmount: "Current year tax amount",
	legal_description: "Legal description of the parcel",
	legalDescription: "Legal description of the parcel",
};

/**
 * Category label or key -> Lucide icon name for the column selection UI.
 * Aligns with ParcelDataViewer categories and API category names.
 */
export const ENRICHMENT_CATEGORY_ICONS: Record<
	string,
	| "User"
	| "DollarSign"
	| "Home"
	| "TrendingUp"
	| "Users"
	| "FileText"
	| "MapPin"
> = {
	// ParcelDataViewer-aligned
	"Property characteristics": "Home",
	property_characteristics: "Home",
	Financial: "DollarSign",
	financial: "DollarSign",
	Sales: "TrendingUp",
	sales: "TrendingUp",
	"Location & address": "MapPin",
	location_address: "MapPin",
	"Owner & mailing": "User",
	owner_mailing: "User",
	"Mailing address": "MapPin",
	mailing_address: "MapPin",
	"Geographic & census": "Users",
	geographic_census: "Users",
	"Legal & assessor": "FileText",
	legal_assessor: "FileText",
	// API / design variants
	"Owner Information": "User",
	owner_information: "User",
	"Identity & location": "MapPin",
	"Identity & Location": "MapPin",
	identity_location: "MapPin",
	"Property Details": "Home",
	"Property attributes": "Home",
	"Property Attributes": "Home",
	property_details: "Home",
	property_attributes: "Home",
	"Market Data": "TrendingUp",
	market_data: "TrendingUp",
	Demographics: "Users",
	demographics: "Users",
	"Tax & Legal": "FileText",
	"Legal & Assessor": "FileText",
	tax_legal: "FileText",
};

export function getColumnDescription(key: string): string | undefined {
	return ENRICHMENT_COLUMN_DESCRIPTIONS[key];
}

/** API appends this suffix to parcel-database column titles in the enrichment picker. */
export function splitEnrichmentColumnDisplayLabel(label: string): {
	primary: string;
	suffix: string | null;
} {
	const m = label.match(/^(.*?)(\s*\(Parcel search\))\s*$/i);
	if (!m) return { primary: label, suffix: null };
	return { primary: m[1].trim(), suffix: m[2] };
}

const COLUMN_LABEL_MAP = (() => {
	const m: Record<string, string> = {};
	for (const cat of PARCEL_DATA_CATEGORIES) {
		for (const col of cat.columns) {
			m[col.key] = col.label;
		}
	}
	return m;
})();

export function getColumnLabel(key: string): string {
	return (
		COLUMN_LABEL_MAP[key] ??
		key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
	);
}
